import { useCallback, useEffect, useRef, useState } from 'react';
import type { MRT_Cell, MRT_Row, MRT_RowData, MRT_TableInstance } from 'material-react-table';
import type { SxProps, Theme } from '@mui/material';

export type CellCoords = {
  rowId: string;
  columnId: string;
};

export interface UseExcelEditingOptions<TData extends MRT_RowData> {
  tableInstanceRef: React.RefObject<MRT_TableInstance<TData> | null>;
  onSaveCell?: (cell: MRT_Cell<TData>, value: any) => boolean | void;
  excludedColumnIds?: string[];
  filteredRows?: TData[];
  allFields?: string[];
  containerRef?: React.RefObject<HTMLDivElement | null>;
}

export interface UseExcelEditingReturn<TData extends MRT_RowData> {
  selectedCell: CellCoords | null;
  setSelectedCell: React.Dispatch<React.SetStateAction<CellCoords | null>>;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  getBodyCellProps: (params: { cell: MRT_Cell<TData> }) => {
    tabIndex: number;
    className: string;
    onClick: () => void;
    onDoubleClick: () => void;
  };
  getEditTextFieldProps: (params: { cell: MRT_Cell<TData> }) => {
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  };
  getContainerProps: () => {
    ref: React.RefObject<HTMLDivElement>;
    tabIndex: number;
    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  };
}

export const excelSelectedCellSx: SxProps<Theme> = {
  '& .excel-cell-selected': {
    outline: '2px solid #1976d2 !important',
    outlineOffset: '-2px',
    backgroundColor: '#e3f2fd !important',
  },
};

export function useExcelEditing<TData extends MRT_RowData>(options: UseExcelEditingOptions<TData>): UseExcelEditingReturn<TData> {
  const { tableInstanceRef, onSaveCell, excludedColumnIds = [], filteredRows, allFields, containerRef } = options;
  const [selectedCell, setSelectedCell] = useState<CellCoords | null>(null);
  const internalContainerRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = (containerRef as React.RefObject<HTMLDivElement> | undefined) ?? internalContainerRef;
  const editingStartedByTypingRef = useRef(false);

  // Clear selected cell if row/column no longer exists
  useEffect(() => {
    if (!selectedCell || !filteredRows || !allFields) return;
    const rowExists = filteredRows.some(row => (row as any).id === selectedCell.rowId);
    const columnExists = excludedColumnIds.includes(selectedCell.columnId) || allFields.includes(selectedCell.columnId);
    if (!rowExists || !columnExists) {
      setSelectedCell(null);
    }
  }, [selectedCell, filteredRows, allFields, excludedColumnIds]);

  const focusTableContainer = useCallback(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.focus({ preventScroll: true });
    }
  }, []);

  const getCellFromCoords = useCallback(
    (coords: CellCoords | null): MRT_Cell<TData> | null => {
      if (!coords) return null;
      const instance = tableInstanceRef.current;
      if (!instance) return null;
      const targetRow = instance.getRowModel().rows.find(r => r.id === coords.rowId);
      if (!targetRow) return null;
      return targetRow.getAllCells().find(c => c.column.id === coords.columnId) ?? null;
    },
    [tableInstanceRef],
  );

  const openCellForEditing = useCallback(
    (cell: MRT_Cell<TData>, options?: { initiatedByTyping?: boolean; initialValue?: string }) => {
      const initiatedByTyping = options?.initiatedByTyping ?? false;
      const initialValue = options?.initialValue;
      editingStartedByTypingRef.current = initiatedByTyping;
      const instance = tableInstanceRef.current;
      if (!instance) return;
      instance.setEditingCell(cell);
      queueMicrotask(() => {
        const input = instance.refs?.editInputRefs?.current?.[cell.column.id] as HTMLInputElement | HTMLTextAreaElement | undefined;
        if (!input) return;
        input.focus();
        if (initiatedByTyping && typeof initialValue === 'string') {
          if (input instanceof HTMLInputElement && input.type === 'number' && initialValue === '-') {
            input.type = 'text';
          }
          const prototype = Object.getPrototypeOf(input);
          const valueSetter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
          if (valueSetter) {
            valueSetter.call(input, initialValue);
          } else {
            (input as HTMLInputElement).value = initialValue;
          }
          const inputEvent = new Event('input', { bubbles: true });
          input.dispatchEvent(inputEvent);
        }
        if (initiatedByTyping) {
          if (input.type !== 'number') {
            const len = input.value.length;
            input.setSelectionRange(len, len);
          }
        } else if (input.type !== 'number') {
          input.select?.();
        }
      });
    },
    [tableInstanceRef],
  );

  const handleCellClick = useCallback(
    (cell: MRT_Cell<TData>) => {
      if (excludedColumnIds.includes(cell.column.id)) return;
      setSelectedCell({ rowId: cell.row.id, columnId: cell.column.id });
      tableInstanceRef.current?.setEditingCell(null);
      focusTableContainer();
      editingStartedByTypingRef.current = false;
    },
    [excludedColumnIds, focusTableContainer, tableInstanceRef],
  );

  const isCellSelected = useCallback(
    (cell: MRT_Cell<TData>) => selectedCell?.rowId === cell.row.id && selectedCell?.columnId === cell.column.id,
    [selectedCell],
  );

  const moveVerticalFromCell = useCallback(
    (cell: MRT_Cell<TData>, direction: 1 | -1 = 1) => {
      const instance = tableInstanceRef.current;
      if (!instance) return;

      const rows = instance.getRowModel().rows;
      const currentIndex = rows.findIndex(r => r.id === cell.row.id);
      if (currentIndex === -1) return;

      const targetIndex = currentIndex + direction;

      if (targetIndex < 0 || targetIndex >= rows.length) {
        instance.setEditingCell(null);
        focusTableContainer();
        return;
      }

      const targetRow = rows[targetIndex];
      const targetCell = targetRow.getAllCells().find(c => c.column.id === cell.column.id);

      if (!targetCell) return;

      // Close current editing cell
      instance.setEditingCell(null);

      // select the next cell
      setSelectedCell({
        rowId: targetCell.row.id,
        columnId: targetCell.column.id,
      });

      // Focus the container so keyboard events are captured
      queueMicrotask(() => {
        focusTableContainer();
      });
    },
    [focusTableContainer, tableInstanceRef],
  );

  const getNavigableCells = useCallback(
    (row: MRT_Row<TData>) => {
      return row.getAllCells().filter(cell => !excludedColumnIds.includes(cell.column.id));
    },
    [excludedColumnIds],
  );

  const moveSelection = useCallback(
    (currentCell: MRT_Cell<TData>, direction: 'up' | 'down' | 'left' | 'right') => {
      const instance = tableInstanceRef.current;
      if (!instance) return;
      const rows = instance.getRowModel().rows;
      const currentRowIndex = rows.findIndex(r => r.id === currentCell.row.id);
      if (currentRowIndex === -1) return;

      const focusCell = (cell: MRT_Cell<TData>) => {
        setSelectedCell({ rowId: cell.row.id, columnId: cell.column.id });
        queueMicrotask(() => focusTableContainer());
      };

      if (direction === 'up' || direction === 'down') {
        const targetRowIndex = currentRowIndex + (direction === 'up' ? -1 : 1);
        if (targetRowIndex < 0 || targetRowIndex >= rows.length) return;
        const targetRow = rows[targetRowIndex];
        const targetCell = targetRow.getAllCells().find(c => c.column.id === currentCell.column.id);
        if (targetCell && !excludedColumnIds.includes(targetCell.column.id)) {
          focusCell(targetCell);
        }
        return;
      }

      const navigableCells = getNavigableCells(currentCell.row);
      const idx = navigableCells.findIndex(c => c.column.id === currentCell.column.id);
      if (idx === -1) return;
      const targetIdx = idx + (direction === 'left' ? -1 : 1);
      if (targetIdx < 0 || targetIdx >= navigableCells.length) return;
      focusCell(navigableCells[targetIdx]);
    },
    [focusTableContainer, getNavigableCells, excludedColumnIds],
  );

  const handleContainerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const instance = tableInstanceRef.current;
      if (!instance || !selectedCell) return;
      const editingCell = instance.getState().editingCell;
      if (editingCell) return;
      const arrowDirections: Record<string, 'up' | 'down' | 'left' | 'right'> = {
        ArrowUp: 'up',
        ArrowDown: 'down',
        ArrowLeft: 'left',
        ArrowRight: 'right',
      };
      if (arrowDirections[event.key]) {
        const cell = getCellFromCoords(selectedCell);
        if (!cell) return;
        event.preventDefault();
        moveSelection(cell, arrowDirections[event.key]);
        return;
      }
      const isSingleChar = event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey;
      if (isSingleChar) {
        const cell = getCellFromCoords(selectedCell);
        if (!cell) return;
        event.preventDefault();
        openCellForEditing(cell, { initiatedByTyping: true, initialValue: event.key });
        return;
      }
      if (event.key === 'Enter') {
        const cell = getCellFromCoords(selectedCell);
        if (!cell) return;
        event.preventDefault();
        moveVerticalFromCell(cell, event.shiftKey ? -1 : 1);
      }
    },
    [getCellFromCoords, moveSelection, moveVerticalFromCell, selectedCell],
  );

  const getBodyCellProps = useCallback(
    (params: { cell: MRT_Cell<TData> }) => {
      const { cell } = params;
      const classes = isCellSelected(cell) ? 'excel-cell-selected' : '';
      return {
        tabIndex: 0,
        className: classes,
        onClick: () => handleCellClick(cell),
        onDoubleClick: () => openCellForEditing(cell),
      };
    },
    [handleCellClick, isCellSelected, openCellForEditing],
  );

  const getEditTextFieldProps = useCallback(
    (params: { cell: MRT_Cell<TData> }) => {
      const { cell } = params;
      return {
        onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
          const target = event.currentTarget;
          if (target.dataset.skipBlurSave === 'true') {
            delete target.dataset.skipBlurSave;
          } else {
            onSaveCell?.(cell, target.value);
          }
          tableInstanceRef.current?.setEditingCell(null);
          focusTableContainer();
          editingStartedByTypingRef.current = false;
        },
        onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
          const arrowMap: Record<string, 'up' | 'down' | 'left' | 'right'> = {
            ArrowUp: 'up',
            ArrowDown: 'down',
            ArrowLeft: 'left',
            ArrowRight: 'right',
          };
          const direction = arrowMap[event.key];
          if (direction) {
            if (!editingStartedByTypingRef.current) {
              event.stopPropagation();
              return; // allow caret navigation
            }
            event.preventDefault();
            const target = event.currentTarget;
            target.dataset.skipBlurSave = 'true';
            const arrowSaveResult = onSaveCell?.(cell, target.value);
            if (arrowSaveResult === false) return;
            tableInstanceRef.current?.setEditingCell(null);
            editingStartedByTypingRef.current = false;
            if (direction === 'up' || direction === 'down') {
              moveVerticalFromCell(cell, direction === 'up' ? -1 : 1);
            } else {
              moveSelection(cell, direction);
            }
            return;
          }
          if (event.key === 'Enter') {
            event.preventDefault();
            const target = event.currentTarget;
            target.dataset.skipBlurSave = 'true';
            const enterSaveResult = onSaveCell?.(cell, target.value);
            if (enterSaveResult === false) return;
            moveVerticalFromCell(cell, event.shiftKey ? -1 : 1);
            editingStartedByTypingRef.current = false;
          }
        },
      };
    },
    [onSaveCell, focusTableContainer, moveVerticalFromCell, moveSelection, tableInstanceRef],
  );

  const getContainerProps = useCallback(
    () => ({
      ref: tableContainerRef,
      tabIndex: 0,
      onKeyDown: handleContainerKeyDown,
    }),
    [handleContainerKeyDown],
  );

  return {
    selectedCell,
    setSelectedCell,
    tableContainerRef,
    getBodyCellProps,
    getEditTextFieldProps,
    getContainerProps,
  };
}
